import React, { useCallback, useEffect, useState } from "react";
import { Button, IconButton, Tooltip, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { Link } from "react-router-dom";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import CloudDownload from "@material-ui/icons/CloudDownload";
import { useTranslation } from "react-i18next";
import useStyles from "./Organizations.styles";
import YesNoDialog from "../../../components/YesNoDialog";
import Cookies from "universal-cookie";
import { CSVLink } from "react-csv";
import CustomNoRowsOverlay from "../../../components/CustomNoRowsOverlay";
import UserDataGrid from "../../../components/UserDataGrid";

  const cookies = new Cookies();

  const Organizations = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deletionOrgId, setDeletionOrgId] = useState("");
  const [deleteResult, setDeleteResult] = useState();
  const [data, setData] = useState([]);


  const columns = [
    {
      field: "fullName",
      headerName: t("common.table.header.name"),
      width: 230,
    },
    { field: "email", headerName: t("common.table.header.email"), width: 230 },
    { field: "url", headerName: t("common.table.header.url"), width: 230 },
    {
      field: "createdAt",
      headerName: t("common.table.header.createdAt"),
      width: 150,
      valueFormatter: (params) => new Date(params.value).toLocaleDateString(),
    },
    { field: "status", headerName: t("common.table.header.status"), width: 230,
     },
    {
      field: "id",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <>
          <Tooltip arrow title={t("common.table.action.edit")}>
            <IconButton
              aria-label={t("common.table.action.edit")}
              color="primary"
              component={Link}
              to={`/provider/organizations/${params.value}`}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip arrow title={t("common.table.action.delete")}>
            <IconButton
              aria-label="delete"
              color="primary"
              onClick={() => {
                setOpenDeleteDialog(true);
                setDeletionOrgId(params.value);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const getOrganizations = useCallback(async () => {
    try {
      let response = await fetch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_ORGANIZATION}`,
        {
          headers: {
            "X-ACCESS-TOKEN": cookies.get("token"),
          },
        }
      );

      let json = await response.json();
   
      if (!response.ok) {
        setError(json.message);
        return;
      }
      setOrganizations(
        json.map((user, index) => {
          return {
            id: user._id,
            fullName: `${user.name}`,
            url: user.url,
            createdAt: user.createdAt,
            email: user.email,
            status: user.isActive ? "Active" : "InActive"
          };
        })
      );

      setData(
        json.map((patient, index) => {
          return {
            Email: patient.email,
            Name: patient.name,
            CreationDate: patient.createdAt,
            Url: patient.url,
            Status: patient.isActive ? "Active" : "InActive",
          };
        })
      );
    } catch (error) {
      console.log(error);
      setError(
        `${t(
          "module.provider.listOrganizations.message.fetchDataFailure"
        )}. ${t("common.message.error.seeLog")}.`
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    getOrganizations();
  }, [getOrganizations]);

  const handleDeleteDialogAnswer = (answer) => async (e) => {
    setOpenDeleteDialog(false);
    if (answer !== "yes") return;

    try {
      let response = await fetch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_ORGANIZATION}/${deletionOrgId}`,
        {
          method: "delete",
          headers: {
            "X-ACCESS-TOKEN": cookies.get("token"),
          },
        }
      );
      if (!response.ok) {
        let json = await response.json();
        setDeleteResult({
          severity: "error",
          message: json.message,
        });
      }
      setDeleteResult({
        severity: "success",
        message: t(
          "module.provider.listOrganizations.message.deleteOrganizationSuccess"
        ),
      });
      getOrganizations();
    } catch (error) {
      console.log(error);
      setDeleteResult({
        severity: "error",
        message: `${t(
          "module.provider.listOrganizations.message.deleteOrganizationFailure"
        )}. ${t("common.message.error.seeLog")}`,
      });
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <>
      <Typography variant="h4" className={classes.title}>
        {t("module.provider.listOrganizations.title")}
      </Typography>
      {deleteResult && (
        <Alert severity={deleteResult.severity}>{deleteResult.message}</Alert>
      )}

      <Button
        variant="contained"
        color="primary"
        className={classes.addButton}
        component={Link}
        to="/provider/organizations/add"
        startIcon={<AddIcon />}
      >
        {t("common.form.button.addNew")}
      </Button>

      {data.length>0 && <Button
        variant="contained"
        color="primary"
        className={classes.exportButton}
        startIcon={<CloudDownload />}
        style={{ marginBottom: "8px", marginLeft: "16px" }}
      >
        <CSVLink
          className={classes.csvExportBtn}
          data={data}
          filename={"organizations.csv"}
          style={{ color: "white", textDecoration: "none" }}
        >
          Export
        </CSVLink>
      </Button>}

      <UserDataGrid
        rows={organizations}
        columns={columns}
        loading={loading}
        components={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
      />
      <YesNoDialog
        title="Are you sure?"
        text="This will remove the organization and it's profiles"
        open={openDeleteDialog}
        onAnswer={handleDeleteDialogAnswer}
      />
    </>
  );
};
export default Organizations;
